import { useId } from "react"
import type { FC, ReactElement } from "react"

import eq from "lodash-es/eq"
import isEmpty from "lodash-es/isEmpty"
import map from "lodash-es/map"

import {
    EVerticalProgressBarTooltipVisibilityStatus as EProgressBarTooltipVisibility,
    ETypographyColor,
    ETypographySize,
    ETypographyWeight,
    VerticalProgressBar as ProgressBar,
    Typography
} from "@/3514/components"
import {
    type TScreenBreakpoints,
    type TUseInteractionState,
    useInteractionState,
    useScreenBreakpoints
} from "@/3514/hooks"
import {
    buildCssClassName,
    getVerticalProgressBarColorStatusEnumByModulePhaseTrackStatusEnum as getBarColorStatus,
    getVerticalProgressBarFillLevelEnumByModulePhaseProgressStatusEnum as getBarFillLevel
} from "@/3514/utils"
import { type TGetValueFromTranslatedObjectFunction, useGetValueFromTranslatedObject } from "@/hooks"
import { emptyCallback } from "@/shared/types/functions"

import { moduleExcerptConfig as config } from "./module-excerpt.config"
import { moduleExcerptHelpers as helpers } from "./module-excerpt.helpers"
import { moduleExcerptStyles as classes } from "./module-excerpt.styles"
import {
    EModuleExcerptRsvpButtonPosition as EButtonPosition,
    EModuleExcerptMode as EMode,
    EModuleExcerptVariant as EVariant,
    type TModuleExcerptPhase as TPhase,
    type TModuleExcerptUiModel as TProps
} from "./module-excerpt.types"

const { barVariantsMap, barStarVisibilitiesMap, barInteractivityMap, barAccessibilityMap } = config

const ModuleExcerpt: FC<TProps> = ({
    // api part
    module: { rank, name, isStub = false, session = null, phases = [] },
    // ui part
    mode = EMode.Full,
    variant = EVariant.Full,
    rsvpButtonPosition = EButtonPosition.Inside,
    containerClassName = String(""),
    wrapperClassName = String(""),
    isLastOrdered = false,
    isCurrentModule = false,
    titleSize = ETypographySize.Tiny,
    titleColor = ETypographyColor.Dark,
    withPaddings = false,
    withBottomBorder = false,
    handleSessionAction = emptyCallback
}: TProps): ReactElement => {
    const itemId: string = useId()

    const getValueFromTranslatedObject: TGetValueFromTranslatedObjectFunction = useGetValueFromTranslatedObject()

    const breakpoints: TScreenBreakpoints = useScreenBreakpoints()

    const {
        isActionAffected,
        actions: { onMouseEnter, onMouseLeave, onFocus, onBlur, onClick, onKeyDown }
    }: TUseInteractionState = useInteractionState()

    const isFullMode: boolean = eq(mode, EMode.Full)

    const isMobile: boolean = breakpoints.xs || breakpoints.sm

    return isEmpty(phases) ? null : (
        <div
            className={buildCssClassName(
                classes.container({ isFullMode, withBorder: !isLastOrdered && isMobile && isFullMode }),
                {
                    [containerClassName]: containerClassName
                }
            )}
        >
            <div
                role="menuitem"
                tabIndex={0}
                data-index={String(rank)}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                onFocus={onFocus}
                onBlur={onBlur}
                onClick={onClick}
                onKeyDown={onKeyDown}
                className={buildCssClassName(
                    classes.wrapper({
                        isFullMode,
                        withPaddings,
                        isLastOrdered,
                        withBottomBorder,
                        isCurrentModule,
                        isActionAffected: isCurrentModule ? false : isActionAffected
                    }),
                    { [wrapperClassName]: wrapperClassName }
                )}
            >
                {isFullMode ? (
                    <Typography
                        text={`${rank}: ${getValueFromTranslatedObject(name)}`}
                        color={
                            isMobile
                                ? ETypographyColor.Gray23
                                : isStub
                                  ? ETypographyColor.Gray300
                                  : isActionAffected && !isStub
                                    ? ETypographyColor.Dark
                                    : titleColor
                        }
                        size={isMobile ? ETypographySize.ExtraSmall : titleSize}
                        weight={isMobile ? ETypographyWeight.Default : ETypographyWeight.Bold}
                        className={classes.title()}
                    />
                ) : null}

                <div className={classes.phasesContainer()}>
                    <div className={classes.phasesWrapper()}>
                        {map<TPhase, ReactElement>(
                            phases,
                            ({ order, progressStatus, trackStatus }: TPhase): ReactElement => (
                                <ProgressBar
                                    key={`chapter-bar-${itemId}-${order}`}
                                    fillLevel={getBarFillLevel(progressStatus)}
                                    colorStatus={getBarColorStatus(trackStatus)}
                                    variant={barVariantsMap[variant]}
                                    starVisibilityStatus={barStarVisibilitiesMap[variant]({
                                        isMobile
                                    })}
                                    interactivityStatus={barInteractivityMap[variant]({
                                        isAllowed: isActionAffected || isCurrentModule
                                    })}
                                    accessibilityStatus={barAccessibilityMap[variant]({
                                        isAllowed: isStub
                                    })}
                                    tooltipConfig={{
                                        visibility: EProgressBarTooltipVisibility.Hidden
                                    }}
                                />
                            )
                        )}
                    </div>

                    {helpers.renderRsvpButton({
                        isVisible: isFullMode ? eq(rsvpButtonPosition, EButtonPosition.Inside) && isMobile : false,
                        session,
                        handleSessionAction,
                        isStub,
                        isFullMode,
                        isCurrentModule
                    })}
                </div>
            </div>

            {helpers.renderRsvpButton({
                isVisible: isFullMode ? !isMobile : eq(rsvpButtonPosition, EButtonPosition.Outside),
                session,
                handleSessionAction,
                isStub,
                isFullMode,
                isCurrentModule
            })}
        </div>
    )
}

ModuleExcerpt.displayName = "ModuleExcerpt"

export { ModuleExcerpt }
